import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Skeleton,
  Fade,
  Paper,
  Pagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  FileText,
  Plus,
  Search,
  Edit,
  RefreshCw,
  FileSpreadsheet,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Upload,
  AlertCircle,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../components/common/Notification';
import { certificatesService } from '../../services/certificates';
import {
  CreateCertificateDialog,
  EditCertificateDialog,
  useCertificateDialog,
} from './CertificateForms';
import CustomButton from '../../components/ui/CustomButton';
import * as XLSX from 'xlsx';
import DirectCOCUploadDialog from '../../components/common/DirectCOCUploadDialog';

// Import the new tabbed ViewCertificateDialog
import ViewCertificateDialog from './ViewCerificateDialog';
// Import the complete CertificateCard component
import CertificateCard from '../../components/common/CertificateCard';
import DraftToProductionModal from '../../components/common/DratfToProdModal';

const CertificatesManagementPage = () => {
  const { isAdmin, isOffice } = useAuth();
  const { isDarkMode } = useTheme();
  const { showNotification, NotificationComponent } = useNotification();

  // Certificate dialog management
  const {
    createDialogOpen,
    editDialogOpen,
    selectedCertificate,
    setSelectedCertificate,
    openCreateDialog,
    closeCreateDialog,
    openEditDialog,
    closeEditDialog,
  } = useCertificateDialog();

  const [allCertificates, setAllCertificates] = useState([]); // Store all certificates
  const [filteredCertificates, setFilteredCertificates] = useState([]); // Store filtered results
  const [displayedCertificates, setDisplayedCertificates] = useState([]); // Store current page certificates
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isReCertificateFilter, setIsReCertificateFilter] = useState(null); // null = all, true = reissued only, false = original only
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(20); // User can now change this
  const [isExporting, setIsExporting] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    drafts: 0,
    production: 0,
    thisMonth: 0,
  });

  // Draft to Production conversion state
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [certificateToConvert, setCertificateToConvert] = useState(null);
  const [directUploadDialogOpen, setDirectUploadDialogOpen] = useState(false);

  // Reissue confirmation dialog state
  const [reissueDialogOpen, setReissueDialogOpen] = useState(false);
  const [certificateToReissue, setCertificateToReissue] = useState(null);
  const [isReissuing, setIsReissuing] = useState(false);

  // FIXED: Fetch all certificates from API
  const fetchCertificates = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await certificatesService.getCertificates({
        pageSize: 10000, // Get all certificates
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });

      // Handle different response structures - API returns flat array
      let certificatesData = [];
      if (Array.isArray(response)) {
        certificatesData = response;
      } else if (response.data && Array.isArray(response.data)) {
        certificatesData = response.data;
      } else if (
        response.certificates &&
        Array.isArray(response.certificates)
      ) {
        certificatesData = response.certificates;
      }

      setAllCertificates(certificatesData);

      // Calculate statistics from loaded data
      calculateStats(certificatesData);
    } catch (err) {
      setError(err.message || 'Failed to fetch certificates');
      showNotification('Failed to load certificates', 'error');
      setAllCertificates([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDirectCOCUpload = async (cocNumber, files, titles) => {
    try {
      const result = await certificatesService.uploadAttachmentsDirect(
        cocNumber,
        files,
        titles
      );

      showNotification(
        `Successfully uploaded ${files.length} file(s) to certificate ${cocNumber}`,
        'success'
      );

      // Refresh certificates to show any updates
      fetchCertificates();

      return Promise.resolve(result);
    } catch (error) {
      const errorMessage = error.message || 'Failed to upload attachments';
      showNotification(errorMessage, 'error');
      return Promise.reject(error);
    }
  };

  // FIXED: Calculate statistics from certificates data
  const calculateStats = certificates => {
    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const stats = {
      total: certificates.length,
      drafts: certificates.filter(cert => cert.IsDraft === true).length,
      production: certificates.filter(cert => cert.IsDraft === false).length,
      thisMonth: certificates.filter(cert => {
        const createdDate = new Date(cert.CreatedAt);
        return createdDate >= thisMonthStart;
      }).length,
      expired: certificates.filter(cert => {
        if (cert.IsDraft) return false;
        if (!cert.ExpiryDate || cert.ExpiryDate === '0001-01-01T00:00:00')
          return false;
        return new Date(cert.ExpiryDate) < now;
      }).length,
    };

    stats.approved = stats.production;
    stats.pending = stats.drafts;

    setStats(stats);
  };

  // FIXED: Enhanced search function
  const searchCertificates = (certificates, searchTerm) => {
    if (!searchTerm) return certificates;

    const term = searchTerm.toLowerCase();
    return certificates.filter(cert => {
      // Search in multiple fields
      const searchFields = [
        cert.CocNumber || '',
        cert.ReferenceNumber || '',
        cert.ImporterName || '',
        cert.ExporterName || '',
        cert.InvoiceNumber || '',
        cert.DocumentNumber || '',
        `Draft-${cert.Id}`, // For draft certificates
      ];

      return searchFields.some(field =>
        field.toString().toLowerCase().includes(term)
      );
    });
  };

  // FIXED: Filter by status using IsDraft boolean
  const filterByStatus = (certificates, status) => {
    if (status === 'all') return certificates;

    const now = new Date();

    switch (status) {
      case 'draft':
        return certificates.filter(cert => cert.IsDraft === true);
      case 'production':
        return certificates.filter(cert => cert.IsDraft === false);
      case 'active':
        return certificates.filter(cert => {
          if (cert.IsDraft) return false;
          if (!cert.ExpiryDate || cert.ExpiryDate === '0001-01-01T00:00:00')
            return true;
          return new Date(cert.ExpiryDate) >= now;
        });
      case 'expired':
        return certificates.filter(cert => {
          if (cert.IsDraft) return false;
          if (!cert.ExpiryDate || cert.ExpiryDate === '0001-01-01T00:00:00')
            return false;
          return new Date(cert.ExpiryDate) < now;
        });
      default:
        return certificates;
    }
  };

  // Filter by reissue status
  const filterByReissue = (certificates, isReCertificate) => {
    if (isReCertificate === null) return certificates;
    return certificates.filter(cert => cert.IsReCertificate === isReCertificate);
  };

  // FIXED: Filter by date ranges
  const filterByDate = (certificates, dateFilter, startDate, endDate) => {
    if (dateFilter === 'all') return certificates;

    const now = new Date();
    let filterStartDate, filterEndDate;

    if (dateFilter === 'custom' && startDate && endDate) {
      filterStartDate = new Date(startDate);
      filterEndDate = new Date(endDate);
      filterEndDate.setHours(23, 59, 59, 999); // End of day
    } else {
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      switch (dateFilter) {
        case 'today':
          filterStartDate = today;
          filterEndDate = new Date(today);
          filterEndDate.setHours(23, 59, 59, 999);
          break;
        case 'week':
          const weekStart = new Date(today);
          weekStart.setDate(today.getDate() - today.getDay()); // Start of week (Sunday)
          filterStartDate = weekStart;
          filterEndDate = new Date(today);
          filterEndDate.setHours(23, 59, 59, 999);
          break;
        case 'month':
          filterStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
          filterEndDate = new Date(today);
          filterEndDate.setHours(23, 59, 59, 999);
          break;
        case 'year':
          filterStartDate = new Date(now.getFullYear(), 0, 1);
          filterEndDate = new Date(today);
          filterEndDate.setHours(23, 59, 59, 999);
          break;
        case 'last7days':
          filterStartDate = new Date(today);
          filterStartDate.setDate(today.getDate() - 7);
          filterEndDate = new Date(today);
          filterEndDate.setHours(23, 59, 59, 999);
          break;
        case 'last30days':
          filterStartDate = new Date(today);
          filterStartDate.setDate(today.getDate() - 30);
          filterEndDate = new Date(today);
          filterEndDate.setHours(23, 59, 59, 999);
          break;
        default:
          return certificates;
      }
    }

    return certificates.filter(cert => {
      const certDate = new Date(cert.CreatedAt);
      return certDate >= filterStartDate && certDate <= filterEndDate;
    });
  };

  // FIXED: Apply all filters and pagination
  const applyFiltersAndPagination = () => {
    let filtered = allCertificates;

    // Apply search
    filtered = searchCertificates(filtered, debouncedSearchTerm);

    // Apply status filter using IsDraft
    filtered = filterByStatus(filtered, statusFilter);

    // Apply date filter
    filtered = filterByDate(filtered, dateFilter, startDate, endDate);

    // Apply reissue filter
    filtered = filterByReissue(filtered, isReCertificateFilter);

    setFilteredCertificates(filtered);
    setTotalCount(filtered.length);

    // Calculate pagination
    const totalPagesCalculated = Math.ceil(filtered.length / pageSize);
    setTotalPages(Math.max(1, totalPagesCalculated));

    // Ensure current page is valid
    const validCurrentPage = Math.min(currentPage, totalPagesCalculated || 1);
    setCurrentPage(validCurrentPage);

    // Get current page data
    const startIndex = (validCurrentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const currentPageData = filtered.slice(startIndex, endIndex);

    setDisplayedCertificates(currentPageData); // Debug log
  };

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Apply filters when dependencies change
  useEffect(() => {
    if (allCertificates.length > 0) {
      setCurrentPage(1); // Reset to first page when filters change
      applyFiltersAndPagination();
    }
  }, [
    debouncedSearchTerm,
    statusFilter,
    dateFilter,
    startDate,
    endDate,
    allCertificates,
    pageSize, // Recalculate when page size changes
    isReCertificateFilter,
  ]);

  // Apply pagination when page changes
  useEffect(() => {
    if (allCertificates.length > 0) {
      applyFiltersAndPagination();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, allCertificates.length]);

  // Initial data fetch
  useEffect(() => {
    fetchCertificates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FIXED: Enhanced export functionality
  const exportToExcel = async () => {
    try {
      setIsExporting(true);

      if (filteredCertificates.length === 0) {
        showNotification('No certificates to export', 'warning');
        return;
      }

      // Transform data for Excel export
      const excelData = filteredCertificates.map((cert, index) => ({
        'No.': index + 1,
        'COC Number': cert.CocNumber || `Draft-${cert.Id}`,
        Status: cert.IsDraft ? 'Draft' : 'Production',
        'Registration Number': cert.RegistrationNumber || '',
        'Reference Number': cert.ReferenceNumber || '',
        'Importer Name': cert.ImporterName || '',
        'Importer Address': cert.ImporterAddress || '',
        'Exporter Name': cert.ExporterName || '',
        'Exporter Country': cert.ExporterCountry || '',
        'Invoice Number': cert.InvoiceNumber || '',
        'Invoice Value': cert.InvoiceValue || 0,
        Currency: cert.InvoiceCurrency || '',
        'Assessment Date': cert.AssessmentDate
          ? new Date(cert.AssessmentDate).toLocaleDateString()
          : '',
        'Issuance Date':
          cert.IssuanceDate && cert.IssuanceDate !== '0001-01-01T00:00:00'
            ? new Date(cert.IssuanceDate).toLocaleDateString()
            : '',
        'Expiry Date':
          cert.ExpiryDate && cert.ExpiryDate !== '0001-01-01T00:00:00'
            ? new Date(cert.ExpiryDate).toLocaleDateString()
            : '',
        'Created Date': cert.CreatedAt
          ? new Date(cert.CreatedAt).toLocaleDateString()
          : '',
      }));

      // Create worksheet and workbook
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Certificates');

      // Set column widths
      const columnWidths = [
        { wch: 5 },
        { wch: 20 },
        { wch: 12 },
        { wch: 20 },
        { wch: 20 },
        { wch: 30 },
        { wch: 40 },
        { wch: 30 },
        { wch: 20 },
        { wch: 20 },
        { wch: 15 },
        { wch: 10 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
      ];
      ws['!cols'] = columnWidths;

      const dateRange =
        startDate && endDate
          ? `_${startDate}_to_${endDate}`
          : statusFilter !== 'all'
            ? `_${statusFilter}`
            : '';
      const filename = `certificates_export${dateRange}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, filename);

      showNotification(
        `Exported ${filteredCertificates.length} certificates successfully`,
        'success'
      );
    } catch (error) {
      showNotification('Failed to export certificates', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  // FIXED: Enhanced pagination handlers
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleFirstPage = () => {
    setCurrentPage(1);
  };

  const handleLastPage = () => {
    setCurrentPage(totalPages);
  };

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Handle certificate actions
  const handleViewCertificate = certificate => {
    setSelectedCertificate(certificate);
    setViewDialogOpen(true);
  };

  // Handle edit certificate
  const handleEditCertificate = certificate => {
    if (!isAdmin() && !isOffice()) {
      showNotification('Only administrators and office users can edit certificates', 'error');
      return;
    }
    openEditDialog(certificate);
  };

  const handleCreateCertificate = async certificateData => {
    try {
      await certificatesService.createCertificate(certificateData);
      fetchCertificates(); // Refresh all data
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const handleUpdateCertificate = async (certificateData, certificateId) => {
    try {
      if (!isAdmin() && !isOffice()) {
        throw new Error('Only administrators and office users can update certificates');
      }
      await certificatesService.updateCertificate(
        certificateId,
        certificateData
      );
      fetchCertificates(); // Refresh all data
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const handleDeleteCertificate = async certificate => {
    try {
      if (!isAdmin()) {
        showNotification(
          'Only administrators can delete certificates',
          'error'
        );
        return Promise.reject(new Error('Insufficient permissions'));
      }
      await certificatesService.deleteCertificate(certificate.Id);
      fetchCertificates(); // Refresh all data
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const handleDownloadCertificate = async certificate => {
    try {
      const isDraft = certificate.IsDraft;
      const blob = await certificatesService.getCertificatePDF(
        certificate.Id,
        isDraft
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const baseFileName =
        certificate.CocNumber || `certificate-${certificate.Id}`;
      const suffix = isDraft ? '_draft' : '';
      link.download = `${baseFileName}${suffix}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const handleConvertToProduction = certificate => {
    setCertificateToConvert(certificate);
    setConvertDialogOpen(true);
  };

  const handleSyncCOCFile = async certificate => {
    try {
      if (!certificate.CocNumber) {
        throw new Error('COC Number not found');
      }
      await certificatesService.syncCOCFile(certificate.CocNumber);
      fetchCertificates(); // Refresh all data
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const handleReissueCertificate = certificate => {
    // Validate before opening dialog
    if (!certificate.IsStage1Sync || !certificate.IsStage2Sync || certificate.IsDraft) {
      showNotification('Only completed certificates can be reissued', 'warning');
      return;
    }
    
    // Open confirmation dialog
    setCertificateToReissue(certificate);
    setReissueDialogOpen(true);
  };

  const handleConfirmReissue = async () => {
    if (!certificateToReissue) return;

    setIsReissuing(true);
    try {
      const reissuedCertificate = await certificatesService.reissueCertificate(certificateToReissue.Id);
      showNotification(
        `Certificate reissued successfully! New COC Number: ${reissuedCertificate.CocNumber}`,
        'success'
      );
      setReissueDialogOpen(false);
      setCertificateToReissue(null);
      fetchCertificates(); // Refresh all data
    } catch (error) {
      const errorMessage = error?.message || error?.toString() || 'Failed to reissue certificate';
      showNotification(errorMessage, 'error');
    } finally {
      setIsReissuing(false);
    }
  };

  const handleCloseReissueDialog = () => {
    if (!isReissuing) {
      setReissueDialogOpen(false);
      setCertificateToReissue(null);
    }
  };

  const handleConfirmConversion = async (issuanceDate, expiryDate) => {
    if (!certificateToConvert) return;

    try {
      await certificatesService.changeDraftStatus(certificateToConvert.Id, {
        issuanceDate,
        expiryDate,
      });

      showNotification(
        'Certificate converted to production successfully! COC Number will be assigned.',
        'success'
      );
      fetchCertificates(); // Refresh all data
      setConvertDialogOpen(false);
      setCertificateToConvert(null);
    } catch (error) {
      showNotification(
        `Failed to convert certificate: ${error.message || 'Unknown error'}`,
        'error'
      );
    }
  };

  const handleCloseConvertModal = () => {
    setConvertDialogOpen(false);
    setCertificateToConvert(null);
  };

  // Statistics cards data
  const statsCards = [
    {
      title: 'Total Certificates',
      value: stats.total || 0,
      icon: FileText,
      color: '#3b82f6',
      change: `+${stats.thisMonth || 0} this month`,
    },
    {
      title: 'Draft Certificates',
      value: stats.drafts || 0,
      icon: Edit,
      color: '#f59e0b',
      change: 'Ready for production',
    },
    {
      title: 'Production Certificates',
      value: stats.production || 0,
      icon: FileText,
      color: '#10b981',
      change: 'Active certificates',
    },
  ];

  return (
    <Box sx={{ backgroundColor: 'transparent' }}>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: isDarkMode ? '#f1f5f9' : '#1e293b',
              textShadow: isDarkMode
                ? '0 0 20px rgba(59, 130, 246, 0.3)'
                : 'none',
            }}
          >
            Certificates Management 📋
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: isDarkMode ? '#94a3b8' : '#64748b',
              fontSize: '1.1rem',
            }}
          >
            Manage certificates of conformity and compliance documents
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <CustomButton
            variant="outlined"
            onClick={fetchCertificates}
            disabled={isLoading}
            startIcon={<RefreshCw size={16} />}
            sx={{
              borderColor: isDarkMode ? '#374151' : '#d1d5db',
              color: isDarkMode ? '#e5e7eb' : '#374151',
              '&:hover': {
                borderColor: '#3b82f6',
                color: '#3b82f6',
                background: 'rgba(59, 130, 246, 0.1)',
              },
            }}
          >
            Refresh
          </CustomButton>

          {/* NEW: Direct Upload Button */}
          <CustomButton
            variant="outlined"
            onClick={() => setDirectUploadDialogOpen(true)}
            startIcon={<Upload size={16} />}
            sx={{
              borderColor: '#8b5cf6',
              color: '#8b5cf6',
              '&:hover': {
                borderColor: '#7c3aed',
                color: '#7c3aed',
                background: 'rgba(139, 92, 246, 0.1)',
              },
            }}
          >
            Upload by COC
          </CustomButton>

          <CustomButton
            variant="gradient"
            gradient="success"
            onClick={exportToExcel}
            disabled={isExporting || totalCount === 0}
            startIcon={
              isExporting ? (
                <RefreshCw size={16} className="animate-spin" />
              ) : (
                <FileSpreadsheet size={16} />
              )
            }
            sx={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                boxShadow: '0 8px 25px rgba(16, 185, 129, 0.6)',
                transform: 'translateY(-2px)',
              },
              '&:disabled': { opacity: 0.6, transform: 'none' },
            }}
          >
            {isExporting ? 'Exporting...' : 'Export Excel'}
          </CustomButton>

          {isOffice() && (
            <CustomButton
              variant="gradient"
              gradient="primary"
              onClick={openCreateDialog}
              startIcon={<Plus size={18} />}
              sx={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)',
                '&:hover': {
                  background:
                    'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                  boxShadow: '0 8px 25px rgba(59, 130, 246, 0.6)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Create Certificate
            </CustomButton>
          )}
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <CustomButton
              size="small"
              onClick={fetchCertificates}
              startIcon={<RefreshCw size={14} />}
            >
              Retry
            </CustomButton>
          }
        >
          <Typography variant="body2">
            <strong>Error loading certificates:</strong> {error}
          </Typography>
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            {isLoading ? (
              <Card
                sx={{
                  height: 140,
                  background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                  backdropFilter: 'blur(20px)',
                  border: isDarkMode
                    ? '1px solid rgba(148, 163, 184, 0.1)'
                    : '1px solid rgba(148, 163, 184, 0.2)',
                  borderRadius: 3,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>
                      <Skeleton variant="text" width="60%" height={32} />
                      <Skeleton variant="text" width="80%" height={20} />
                    </Box>
                    <Skeleton variant="circular" width={48} height={48} />
                  </Box>
                  <Skeleton
                    variant="text"
                    width="70%"
                    height={16}
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
            ) : (
              <Fade in={true} timeout={300 + index * 100}>
                <div>
                  <Card
                    sx={{
                      height: 140,
                      background: isDarkMode
                        ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
                        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                      backdropFilter: 'blur(20px)',
                      border: isDarkMode
                        ? '1px solid rgba(148, 163, 184, 0.1)'
                        : '1px solid rgba(148, 163, 184, 0.2)',
                      boxShadow: isDarkMode
                        ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                        : '0 8px 32px rgba(0, 0, 0, 0.1)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: isDarkMode
                          ? `0 20px 40px rgba(0, 0, 0, 0.4), 0 0 30px ${stat.color}20`
                          : `0 20px 40px rgba(0, 0, 0, 0.15), 0 0 30px ${stat.color}20`,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 1,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 700,
                              color: stat.color,
                              mb: 0.5,
                              textShadow: `0 0 20px ${stat.color}40`,
                            }}
                          >
                            {stat.value}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: isDarkMode ? '#94a3b8' : '#64748b',
                              fontWeight: 500,
                            }}
                          >
                            {stat.title}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: `linear-gradient(135deg, ${stat.color}20, ${stat.color}10)`,
                            border: `1px solid ${stat.color}30`,
                            boxShadow: `0 8px 16px ${stat.color}30`,
                          }}
                        >
                          {stat.icon && (
                            <stat.icon size={20} color={stat.color} />
                          )}
                        </Box>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: isDarkMode ? '#cbd5e1' : '#475569',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                        }}
                      >
                        {stat.change}
                      </Typography>
                    </CardContent>
                  </Card>
                </div>
              </Fade>
            )}
          </Grid>
        ))}
      </Grid>

      {/* Search and Filters */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
          backdropFilter: 'blur(20px)',
          border: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
          boxShadow: isDarkMode
            ? '0 8px 32px rgba(0, 0, 0, 0.3)'
            : '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by COC, reference, importer, exporter, or Draft-ID..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search
                      size={20}
                      color={isDarkMode ? '#94a3b8' : '#64748b'}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.5)'
                    : 'rgba(255, 255, 255, 0.5)',
                  '&:hover': {
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.7)'
                      : 'rgba(255, 255, 255, 0.7)',
                  },
                },
              }}
            />
          </Grid>

          {/* FIXED: Status Filter based on IsDraft */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                label="Status"
                sx={{
                  borderRadius: 2,
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.5)'
                    : 'rgba(255, 255, 255, 0.5)',
                }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="draft">📝 Drafts</MenuItem>
                <MenuItem value="production">✅ Production</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Reissue Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Reissue Status</InputLabel>
              <Select
                value={isReCertificateFilter === null ? 'all' : isReCertificateFilter ? 'reissued' : 'original'}
                onChange={e => {
                  const value = e.target.value;
                  setIsReCertificateFilter(value === 'all' ? null : value === 'reissued');
                }}
                label="Reissue Status"
                sx={{
                  borderRadius: 2,
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.5)'
                    : 'rgba(255, 255, 255, 0.5)',
                }}
              >
                <MenuItem value="all">All Certificates</MenuItem>
                <MenuItem value="original">Original Only</MenuItem>
                <MenuItem value="reissued">🔄 Reissued Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* FIXED: Enhanced Date Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Date Filter</InputLabel>
              <Select
                value={dateFilter}
                onChange={e => setDateFilter(e.target.value)}
                label="Date Filter"
                sx={{
                  borderRadius: 2,
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.5)'
                    : 'rgba(255, 255, 255, 0.5)',
                }}
              >
                <MenuItem value="all">All Dates</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
                <MenuItem value="year">This Year</MenuItem>
                <MenuItem value="last7days">Last 7 Days</MenuItem>
                <MenuItem value="last30days">Last 30 Days</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Custom Date Range */}
          {dateFilter === 'custom' && (
            <>
              <Grid item xs={12} sm={6} md={1.5}>
                <TextField
                  label="Start Date"
                  type="date"
                  fullWidth
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: isDarkMode
                        ? 'rgba(30, 41, 59, 0.5)'
                        : 'rgba(255, 255, 255, 0.5)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={1.5}>
                <TextField
                  label="End Date"
                  type="date"
                  fullWidth
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: isDarkMode
                        ? 'rgba(30, 41, 59, 0.5)'
                        : 'rgba(255, 255, 255, 0.5)',
                    },
                  }}
                />
              </Grid>
            </>
          )}
        </Grid>

        {/* FIXED: Enhanced pagination info and controls */}
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography
                variant="body2"
                sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
              >
                Showing {displayedCertificates.length} of {totalCount}{' '}
                certificates
                {(searchTerm ||
                  statusFilter !== 'all' ||
                  dateFilter !== 'all' ||
                  isReCertificateFilter !== null) && (
                  <Chip
                    label="Filtered"
                    size="small"
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                )}
                {startDate && endDate && (
                  <Chip
                    label={`${startDate} to ${endDate}`}
                    size="small"
                    color="secondary"
                    sx={{ ml: 1 }}
                    onDelete={() => {
                      setStartDate('');
                      setEndDate('');
                      setDateFilter('all');
                    }}
                  />
                )}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: isDarkMode ? '#64748b' : '#94a3b8',
                  display: 'block',
                  mt: 0.5,
                }}
              >
                Page {currentPage} of {totalPages}
              </Typography>
            </Box>

            {/* Page Size Selector */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: isDarkMode ? '#94a3b8' : '#64748b',
                  whiteSpace: 'nowrap',
                }}
              >
                Per page:
              </Typography>
              <FormControl size="small">
                <Select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  sx={{
                    minWidth: 70,
                    height: 32,
                    borderRadius: 1.5,
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.5)'
                      : 'rgba(255, 255, 255, 0.5)',
                    '& .MuiSelect-select': {
                      py: 0.5,
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: isDarkMode ? '#e2e8f0' : '#1e293b',
                    },
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={30}>30</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* FIXED: Enhanced pagination controls */}
          {totalPages > 1 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                background: isDarkMode
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)'
                  : 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
                padding: '8px 12px',
                borderRadius: 2,
                border: isDarkMode
                  ? '1px solid rgba(59, 130, 246, 0.2)'
                  : '1px solid rgba(59, 130, 246, 0.1)',
              }}
            >
              {/* First page button */}
              <IconButton
                onClick={handleFirstPage}
                disabled={currentPage === 1}
                size="small"
                sx={{
                  color: isDarkMode ? '#e2e8f0' : '#475569',
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.5)'
                    : 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    background: 'rgba(59, 130, 246, 0.2)',
                    color: '#3b82f6',
                  },
                  '&:disabled': {
                    opacity: 0.3,
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.3)'
                      : 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                <ChevronsLeft size={18} />
              </IconButton>

              {/* Previous page button */}
              <IconButton
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                size="small"
                sx={{
                  color: isDarkMode ? '#e2e8f0' : '#475569',
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.5)'
                    : 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    background: 'rgba(59, 130, 246, 0.2)',
                    color: '#3b82f6',
                  },
                  '&:disabled': {
                    opacity: 0.3,
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.3)'
                      : 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                <ChevronLeft size={18} />
              </IconButton>

              {/* Page numbers */}
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="medium"
                hidePrevButton
                hideNextButton
                siblingCount={1}
                boundaryCount={1}
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: isDarkMode ? '#e2e8f0' : '#475569',
                    fontWeight: 600,
                    minWidth: 36,
                    height: 36,
                    fontSize: '0.875rem',
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.5)'
                      : 'rgba(255, 255, 255, 0.8)',
                    border: isDarkMode
                      ? '1px solid rgba(148, 163, 184, 0.2)'
                      : '1px solid rgba(148, 163, 184, 0.3)',
                    '&.Mui-selected': {
                      background:
                        'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                      color: 'white',
                      border: 'none',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                      fontWeight: 700,
                      '&:hover': {
                        background:
                          'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                      },
                    },
                    '&:hover': {
                      background: isDarkMode
                        ? 'rgba(59, 130, 246, 0.2)'
                        : 'rgba(59, 130, 246, 0.1)',
                      borderColor: '#3b82f6',
                      color: '#3b82f6',
                    },
                  },
                }}
              />

              {/* Next page button */}
              <IconButton
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                size="small"
                sx={{
                  color: isDarkMode ? '#e2e8f0' : '#475569',
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.5)'
                    : 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    background: 'rgba(59, 130, 246, 0.2)',
                    color: '#3b82f6',
                  },
                  '&:disabled': {
                    opacity: 0.3,
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.3)'
                      : 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                <ChevronRight size={18} />
              </IconButton>

              {/* Last page button */}
              <IconButton
                onClick={handleLastPage}
                disabled={currentPage === totalPages}
                size="small"
                sx={{
                  color: isDarkMode ? '#e2e8f0' : '#475569',
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.5)'
                    : 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    background: 'rgba(59, 130, 246, 0.2)',
                    color: '#3b82f6',
                  },
                  '&:disabled': {
                    opacity: 0.3,
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.3)'
                      : 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                <ChevronsRight size={18} />
              </IconButton>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Certificates Grid */}
      {isLoading ? (
        <Grid container spacing={3}>
          {Array(Math.min(pageSize, 12))
            .fill({})
            .map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: 320,
                    background: isDarkMode
                      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
                      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: isDarkMode
                      ? '1px solid rgba(148, 163, 184, 0.1)'
                      : '1px solid rgba(148, 163, 184, 0.2)',
                    borderRadius: 3,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="70%" height={24} />
                        <Skeleton variant="text" width="50%" height={20} />
                      </Box>
                    </Box>
                    {Array(6)
                      .fill({})
                      .map((_, i) => (
                        <Skeleton
                          key={i}
                          variant="text"
                          width="90%"
                          height={20}
                          sx={{ mb: 1 }}
                        />
                      ))}
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>
      ) : displayedCertificates.length > 0 ? (
        <Grid container spacing={3}>
          {displayedCertificates.map((certificate) => (
            <Grid item xs={12} sm={6} md={4} key={certificate.Id}>
              <CertificateCard
                certificate={certificate}
                onView={handleViewCertificate}
                onEdit={handleEditCertificate}
                onDelete={handleDeleteCertificate}
                onDownload={handleDownloadCertificate}
                onConvertToProduction={handleConvertToProduction}
                onSyncCOCFile={handleSyncCOCFile}
                onReissue={handleReissueCertificate}
                isDarkMode={isDarkMode}
                isOffice={isOffice()}
                isAdmin={isAdmin()}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <FileText
            size={64}
            color={isDarkMode ? '#64748b' : '#94a3b8'}
            style={{ marginBottom: 16 }}
          />
          <Typography
            variant="h6"
            sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', mb: 1 }}
          >
            No certificates found
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: isDarkMode ? '#64748b' : '#94a3b8', mb: 3 }}
          >
            {searchTerm || statusFilter !== 'all' || dateFilter !== 'all'
              ? 'Try adjusting your search filters or clear all filters'
              : 'Start by creating your first certificate'}
          </Typography>
          {(searchTerm || statusFilter !== 'all' || dateFilter !== 'all') && (
            <CustomButton
              variant="outlined"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setDateFilter('all');
                setStartDate('');
                setEndDate('');
                setIsReCertificateFilter(null);
              }}
              sx={{ mr: 2 }}
            >
              Clear Filters
            </CustomButton>
          )}
          {!searchTerm &&
            statusFilter === 'all' &&
            dateFilter === 'all' &&
            isReCertificateFilter === null &&
            isOffice() && (
              <CustomButton
                variant="gradient"
                gradient="primary"
                startIcon={<Plus size={16} />}
                onClick={openCreateDialog}
              >
                Create First Certificate
              </CustomButton>
            )}
        </Box>
      )}

      {/* Bottom Pagination - For easier navigation */}
      {!isLoading && displayedCertificates.length > 0 && totalPages > 1 && (
        <Box
          sx={{
            mt: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Paper
            sx={{
              p: 2,
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
              backdropFilter: 'blur(20px)',
              border: isDarkMode
                ? '1px solid rgba(148, 163, 184, 0.1)'
                : '1px solid rgba(148, 163, 184, 0.2)',
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              {/* Page info */}
              <Typography
                variant="body2"
                sx={{
                  color: isDarkMode ? '#94a3b8' : '#64748b',
                  fontWeight: 500,
                }}
              >
                Page {currentPage} of {totalPages}
              </Typography>

              {/* Pagination controls */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)'
                    : 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
                  padding: '6px 10px',
                  borderRadius: 1.5,
                  border: isDarkMode
                    ? '1px solid rgba(59, 130, 246, 0.2)'
                    : '1px solid rgba(59, 130, 246, 0.1)',
                }}
              >
                <IconButton
                  onClick={handleFirstPage}
                  disabled={currentPage === 1}
                  size="small"
                  sx={{
                    color: isDarkMode ? '#e2e8f0' : '#475569',
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.5)'
                      : 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      background: 'rgba(59, 130, 246, 0.2)',
                      color: '#3b82f6',
                    },
                    '&:disabled': { opacity: 0.3 },
                  }}
                >
                  <ChevronsLeft size={16} />
                </IconButton>

                <IconButton
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  size="small"
                  sx={{
                    color: isDarkMode ? '#e2e8f0' : '#475569',
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.5)'
                      : 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      background: 'rgba(59, 130, 246, 0.2)',
                      color: '#3b82f6',
                    },
                    '&:disabled': { opacity: 0.3 },
                  }}
                >
                  <ChevronLeft size={16} />
                </IconButton>

                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                  hidePrevButton
                  hideNextButton
                  siblingCount={0}
                  boundaryCount={1}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: isDarkMode ? '#e2e8f0' : '#475569',
                      fontWeight: 600,
                      minWidth: 32,
                      height: 32,
                      fontSize: '0.8rem',
                      background: isDarkMode
                        ? 'rgba(30, 41, 59, 0.5)'
                        : 'rgba(255, 255, 255, 0.8)',
                      border: isDarkMode
                        ? '1px solid rgba(148, 163, 184, 0.2)'
                        : '1px solid rgba(148, 163, 184, 0.3)',
                      '&.Mui-selected': {
                        background:
                          'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                        color: 'white',
                        border: 'none',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                        fontWeight: 700,
                      },
                      '&:hover': {
                        background: 'rgba(59, 130, 246, 0.2)',
                        borderColor: '#3b82f6',
                        color: '#3b82f6',
                      },
                    },
                  }}
                />

                <IconButton
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  size="small"
                  sx={{
                    color: isDarkMode ? '#e2e8f0' : '#475569',
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.5)'
                      : 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      background: 'rgba(59, 130, 246, 0.2)',
                      color: '#3b82f6',
                    },
                    '&:disabled': { opacity: 0.3 },
                  }}
                >
                  <ChevronRight size={16} />
                </IconButton>

                <IconButton
                  onClick={handleLastPage}
                  disabled={currentPage === totalPages}
                  size="small"
                  sx={{
                    color: isDarkMode ? '#e2e8f0' : '#475569',
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.5)'
                      : 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      background: 'rgba(59, 130, 246, 0.2)',
                      color: '#3b82f6',
                    },
                    '&:disabled': { opacity: 0.3 },
                  }}
                >
                  <ChevronsRight size={16} />
                </IconButton>
              </Box>

              {/* Showing info */}
              <Typography
                variant="caption"
                sx={{
                  color: isDarkMode ? '#64748b' : '#94a3b8',
                }}
              >
                Showing {displayedCertificates.length} of {totalCount}
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}

      {/* View Certificate Dialog */}
      <ViewCertificateDialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        certificate={selectedCertificate}
        isDarkMode={isDarkMode}
      />

      {/* Create Certificate Dialog */}
      <CreateCertificateDialog
        open={createDialogOpen}
        onClose={closeCreateDialog}
        onSave={handleCreateCertificate}
        isDarkMode={isDarkMode}
      />

      {/* Edit Certificate Dialog */}
      <EditCertificateDialog
        open={editDialogOpen}
        onClose={closeEditDialog}
        certificate={selectedCertificate}
        onSave={handleUpdateCertificate}
        isDarkMode={isDarkMode}
      />

      {/* Draft to Production Modal */}
      <DraftToProductionModal
        open={convertDialogOpen}
        onClose={handleCloseConvertModal}
        certificate={certificateToConvert}
        onConvert={handleConfirmConversion}
        isDarkMode={isDarkMode}
      />
      {/* Direct COC Upload Dialog */}
      <DirectCOCUploadDialog
        open={directUploadDialogOpen}
        onClose={() => setDirectUploadDialogOpen(false)}
        onUpload={handleDirectCOCUpload}
        isDarkMode={isDarkMode}
      />

      {/* Reissue Confirmation Dialog */}
      <Dialog
        open={reissueDialogOpen}
        onClose={handleCloseReissueDialog}
        maxWidth="sm"
        PaperProps={{
          sx: {
            background: isDarkMode
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <RefreshCw size={24} color="#3b82f6" />
            <Typography
              variant="h6"
              sx={{
                ml: 1,
                fontWeight: 600,
                color: isDarkMode ? '#f1f5f9' : '#1e293b',
              }}
            >
              Reissue Certificate
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ color: isDarkMode ? '#e2e8f0' : '#374151', mb: 2 }}>
            Are you sure you want to reissue certificate <strong>{certificateToReissue?.CocNumber}</strong>?
          </Typography>
          <Alert 
            severity="info" 
            sx={{ 
              mb: 2,
              backgroundColor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
              color: isDarkMode ? '#93c5fd' : '#1e40af',
            }}
          >
            A new certificate will be created with all the same data. The new certificate will have a COC number in the format: <strong>{certificateToReissue?.CocNumber}-1</strong>
          </Alert>
          <Typography sx={{ color: isDarkMode ? '#cbd5e1' : '#64748b', fontSize: '0.875rem' }}>
            The new certificate will be created as a draft and can be edited before finalizing.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <CustomButton 
            variant="outlined" 
            onClick={handleCloseReissueDialog} 
            disabled={isReissuing}
          >
            Cancel
          </CustomButton>
          <CustomButton
            variant="gradient"
            gradient="primary"
            onClick={handleConfirmReissue}
            disabled={isReissuing}
            startIcon={isReissuing ? <CircularProgress size={16} /> : <RefreshCw size={16} />}
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                boxShadow: '0 8px 25px rgba(59, 130, 246, 0.6)',
              },
            }}
          >
            {isReissuing ? 'Reissuing...' : 'Confirm Reissue'}
          </CustomButton>
        </DialogActions>
      </Dialog>

      {/* Notification Component */}
      <NotificationComponent />
    </Box>
  );
};

export default CertificatesManagementPage;
